import {zodResolver} from '@hookform/resolvers/zod';
import React from 'react';
import {useForm} from 'react-hook-form';
import {Keyboard, View} from 'react-native';
import * as z from 'zod';

import {LoadingButton} from '../../components/Button';
import {ErrorMessage} from '../../components/Form/ErrorMessage';
import {PasswordInput} from '../../components/Form/PasswordInput';
import {ValidatedField} from '../../components/Form/ValidatedField';
import {Text} from '../../components/Typography';
import {Constants} from '../../constants';
import {ErrorMessages, Strings, texts} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {isHttpError, publicUserApi} from '../../lib/api/apis';
import type {RootStackScreenProps} from '../../navigators/RootStackNavigator';
import {centerText} from '../../styles';
import {LogoHeader, LogoTitle} from '../components';
import {KeyboardAvoidingLayout, TitleContainer} from '../components/composable';
import {authStyles} from './styles';

const schema = z
  .object({
    password: z
      .string()
      .nonempty(ErrorMessages.REQUIRED_PASSWORD)
      .min(8, ErrorMessages.PASSWORD_MINIMUM_ERROR)
      .regex(
        Constants.VALID_PASSWORD_REGEX,
        ErrorMessages.PASSWORD_FORMAT_ERROR,
      ),
    confirmPassword: z
      .string()
      .nonempty(ErrorMessages.REQUIRED_PASSWORD_CONFIRMATION),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: ErrorMessages.PASSWORDS_MATCH_ERROR,
    path: ['confirmPassword'],
  });

type PasswordResetValues = z.infer<typeof schema>;

export interface Props {
  token: string;
}

interface ScreenProps
  extends RootStackScreenProps<typeof Screens.PasswordReset> {}

export const PasswordResetScreen: React.FC<ScreenProps> = ({
  navigation,
  route,
}) => {
  const [expiredLink, setExpiredLink] = React.useState(false);

  const defaultValues: PasswordResetValues = {
    password: '',
    confirmPassword: '',
  };

  const token = route?.params?.token;

  const {
    control,
    handleSubmit,
    setError,
    formState: {isSubmitting, errors},
  } = useForm<PasswordResetValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });

  const onSubmit = React.useCallback(
    async ({password}: PasswordResetValues) => {
      Keyboard.dismiss();

      try {
        const {email} = await publicUserApi
          .resetPassword(
            {id: 'me', resetPasswordDto: {password}},
            {headers: {Authorization: `Bearer ${token}`}},
          )
          .then(r => r.data);

        navigation.navigate(Screens.PasswordConfirmed, {
          email,
        });
      } catch (error) {
        if (!isHttpError(error)) throw error;

        if (error?.response?.data.message === ErrorMessages.FORBIDDEN_RESOURCE)
          return setExpiredLink(true);

        throw new Error(ErrorMessages.NETWORK_ERROR);
      }
    },
    [token, navigation],
  );

  return (
    <KeyboardAvoidingLayout>
      <View style={authStyles.innerContainer}>
        <LogoHeader>
          <TitleContainer>
            <LogoTitle>
              {!expiredLink
                ? Strings.CHANGE_PASSWORD
                : texts.screens.changePassword.linkExpired.title}
            </LogoTitle>
          </TitleContainer>
        </LogoHeader>

        <Text variant="s" style={centerText}>
          {!expiredLink
            ? texts.screens.changePassword.subtitle
            : texts.screens.changePassword.linkExpired.subtitle}
        </Text>

        <View style={authStyles.bodyContainer}>
          {!expiredLink && (
            <>
              <ValidatedField {...{control}} name="password">
                {props => (
                  <PasswordInput
                    invalid={!!errors.password}
                    placeholder="Password"
                    {...props}
                  />
                )}
              </ValidatedField>

              <ValidatedField {...{control}} name="confirmPassword">
                {props => (
                  <PasswordInput
                    invalid={!!errors.confirmPassword}
                    placeholder="Password"
                    {...props}
                  />
                )}
              </ValidatedField>

              {errors.root?.message && (
                <ErrorMessage>{errors.root?.message}</ErrorMessage>
              )}
            </>
          )}

          <LoadingButton
            loading={isSubmitting}
            disabled={!expiredLink ? isSubmitting : false}
            onPress={
              !expiredLink
                ? handleSubmit(values =>
                    onSubmit(values).catch(e => {
                      setError('root', {message: e.message});
                    }),
                  )
                : () => navigation.navigate(Screens.ForgotPassword)
            }>
            {!expiredLink ? Strings.CHANGE_PASSWORD : Strings.REQUEST_LINK}
          </LoadingButton>
        </View>
      </View>
    </KeyboardAvoidingLayout>
  );
};
